// Full-document render of the toppers LandingPage (ROUTABLE): PageSkeleton (the
// React document shell) wrapping the ``landing_page.html`` layout -- the
// promo-intro node, the hero (copy + modular configurator), the promo bar -- around
// the four existing LandingPage islands (hero sidekick, body top, body bottom, value
// props).
//
// Server-only values (image renditions, oscar pricing, ``|richtext``/``|safe`` HTML,
// pdp ``pageurl``) are resolved server-side and threaded as scalar side-channels; the
// raw ``|safe`` promo copy and the ``|richtext`` product title are injected via
// dangerouslySetInnerHTML. The configurator SSR envelope arrives as the nested
// ``configurator`` prop (built from ``build_configurator_ssr_context``, threading the
// route-set ``initial_variant_id``).
//
// ``#landing-page-promo-intro-section`` renders permanently hidden (inline
// ``display: none``) with no JS to reveal it, so the promo-intro overlay is
// inert. It is kept as a faithful, hidden port of the Django markup; the
// ``#intro-promo-holder`` header slot it once targeted stays an empty mount
// point.
import React from "react";
import { useStaticURL } from "tsi-common-react/src/utils/urls";

import { templates } from "@reactivated";

import { LandingPageBodyBottom } from "../components/LandingPageBodyBottom";
import { LandingPageBodyTop } from "../components/LandingPageBodyTop";
import { LandingPageHeroSidekick } from "../components/LandingPageHeroSidekick";
import { LandingPageValueProps } from "../components/LandingPageValueProps";
import { PageSkeleton } from "../components/PageSkeleton";
import { PromoBar } from "../components/PromoBar";
import { Template as ModularConfigurator } from "./ModularConfiguratorTemplate";

// Mutable reactivated props (not Immutable<>): this template composes the in-tree
// SiteHeader / SiteFooter chain via PageSkeleton, which is typed with mutable
// reactivated props. The body islands accept Immutable props, to which these are
// assignable.
type IProps = templates.LandingPageTemplate;

// The hero price copy has TWO variants (unlike HomePage's four -- there is no
// ``hero_description`` branch here): both lead with ``{pdp.title} starting at
// only``. Explicit ``{" "}`` preserves the visible spaces JSX would otherwise drop
// at line breaks. The ``--actuall-price`` typo class is preserved from the
// markup.
const PRICE_STRIKE = "landing-page-hero__copy-desc--strike";
const PRICE_ACTUAL =
    "landing-page-hero__copy-desc--actuall-price landing-page-hero__copy-highlight";

const renderPriceCopy = (props: IProps): React.JSX.Element => {
    const actual = (
        <span className={PRICE_ACTUAL}>{props.hero_price_cosmetic}</span>
    );
    if (props.hero_prices_equal) {
        return (
            <>
                {props.hero_pdp_title} starting at only {actual}
            </>
        );
    }
    return (
        <>
            {props.hero_pdp_title} starting at only{" "}
            <span className="screen-reader-only">Original price: </span>{" "}
            <span className={PRICE_STRIKE}>{props.hero_price_retail}</span>{" "}
            <span className="screen-reader-only">Current price: </span> {actual}
        </>
    );
};

const renderPromoIntro = (
    props: IProps,
    staticURL: string,
): React.JSX.Element | null => {
    if (props.promo_intro_image_url === null) {
        return null;
    }
    return (
        <div id="landing-page-promo-intro-section" style={{ display: "none" }}>
            <div
                className="landing-page-promo-intro"
                style={{
                    backgroundImage: `url(${props.promo_intro_image_url})`,
                }}
            >
                <h1 className="landing-page-promo-intro__title">
                    {props.promo_intro_title}
                </h1>
                <div className="landing-page-promo-intro__content">
                    <div
                        className="landing-page-promo-intro__desc landing-page__desc"
                        dangerouslySetInnerHTML={{
                            __html: props.promo_intro_content_html,
                        }}
                    />
                    <div className="landing-page-promo-intro__button">
                        <button className="landing-page-promo-intro__button-item button button--ming al-lp-promo-top__button">
                            <span>{props.promo_intro_button}</span>
                        </button>
                    </div>
                    <div className="landing-page-promo-intro__arrow">
                        <img
                            className="landing-page-promo-intro__arrow-item responsive-img"
                            src={`${staticURL}img/icons/arrow_down.svg`}
                            alt=""
                            loading="lazy"
                            decoding="async"
                        />
                        <div className="landing-page-promo-intro__click-area" />
                    </div>
                </div>
            </div>
        </div>
    );
};

const renderHero = (props: IProps): React.JSX.Element | null => {
    if (props.hero_image_url === null) {
        return null;
    }
    const { page } = props;
    return (
        <div className="l-full-width">
            <div
                className="landing-page-hero"
                style={{
                    // The double background-image; a missing mobile image
                    // yields a literal empty ``url()``.
                    backgroundImage: `url(${props.hero_image_url}), url(${props.hero_mobile_image_url})`,
                }}
            >
                <div className="landing-page-hero__content">
                    <div className="landing-page-hero__copy">
                        {props.hero_has_pdp ? (
                            <>
                                {props.hero_product_title_html ? (
                                    <div
                                        className="landing-page-hero__product-title"
                                        dangerouslySetInnerHTML={{
                                            __html: props.hero_product_title_html,
                                        }}
                                    />
                                ) : (
                                    <div className="landing-page-hero__product-title">
                                        {props.hero_pdp_title}
                                    </div>
                                )}
                                <h1
                                    className={`landing-page__title landing-page-hero__title landing-page-hero__title--${page.hero_style}`}
                                >
                                    {page.hero_title}
                                </h1>
                                <a
                                    className={`button button--${
                                        page.hero_style === "light"
                                            ? "white"
                                            : "ming"
                                    } landing-page-hero__button al-lp-hero__shop-now`}
                                    href={props.hero_pdp_url}
                                >
                                    <span>SHOP NOW</span>
                                </a>
                                <div
                                    className={`landing-page-hero__copy-desc landing-page-hero__copy-desc--${page.hero_style}`}
                                >
                                    {renderPriceCopy(props)}
                                </div>
                            </>
                        ) : null}
                    </div>
                </div>
            </div>

            {props.configurator !== null ? (
                <div className="landing-page-hero__configurator">
                    <ModularConfigurator {...props.configurator} />
                </div>
            ) : null}
        </div>
    );
};

export const Template = (props: IProps): React.JSX.Element => {
    const { page } = props;
    const staticURL = useStaticURL();

    // ``{% if %}`` guards are StreamValue truthiness (an empty stream is
    // falsy) for the StreamField-backed fields.
    const hasHeroOptions =
        !!page.hero_options && page.hero_options.value.length > 0;
    const hasBody =
        !!page.landing_page_props && page.landing_page_props.value.length > 0;
    const hasValueProps =
        !!page.value_props && page.value_props.value.length > 0;

    return (
        <PageSkeleton pageChrome={props.page_chrome}>
            {renderPromoIntro(props, staticURL)}

            {renderHero(props)}

            {hasHeroOptions ? (
                <LandingPageHeroSidekick
                    page={page}
                    hero_product_pdp_url={props.hero_pdp_url}
                />
            ) : null}

            {hasBody ? <LandingPageBodyTop page={page} /> : null}

            <PromoBar
                items={props.promo_bar}
                className="landing-page-promo-bar l-full-width"
            />

            {hasBody ? (
                <LandingPageBodyBottom
                    page={page}
                    hero_product_pdp_url={props.hero_pdp_url}
                />
            ) : null}

            {hasValueProps ? <LandingPageValueProps page={page} /> : null}
        </PageSkeleton>
    );
};
